import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Sparkles, Wand2, Layers, UserCircle, Loader2, Image as ImageIcon } from 'lucide-react';
import MediaPackGenerator from '../components/media-studio/MediaPackGenerator';
import SuccessVisualGenerator from '../components/media-studio/SuccessVisualGenerator';
import PackCard from '../components/media-studio/PackCard';
import PersonalBrandArchitect from '../components/media-studio/PersonalBrandArchitect';
import { useApi } from '../hooks/useApi';

export default function MediaStudio() {
  const api = useApi();
  const [tab, setTab] = useState('create');
  const [latestPack, setLatestPack] = useState(null);
  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [usage, setUsage] = useState(null);

  const loadPacks = async () => {
    setLoadingPacks(true);
    try {
      const res = await api.get('/media-studio/packs');
      setPacks(res.packs || []);
    } catch (e) { /* silent */ } finally {
      setLoadingPacks(false);
    }
    try {
      const u = await api.get('/media-studio/usage');
      setUsage(u);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    if (tab === 'packs') loadPacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="media-studio-page">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-[#B89B5E] to-[#96793f] shadow-lg">
            <Wand2 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#1A1A1A]">Media Studio</h1>
            <p className="text-sm text-muted-foreground">Turn your intelligence and brand into finished marketing content.</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="ms-tabs">
            <TabsTrigger value="create" data-testid="ms-tab-create"><Sparkles className="h-4 w-4 mr-1" />Create</TabsTrigger>
            <TabsTrigger value="success" data-testid="ms-tab-success"><ImageIcon className="h-4 w-4 mr-1" />Success Visual</TabsTrigger>
            <TabsTrigger value="packs" data-testid="ms-tab-packs"><Layers className="h-4 w-4 mr-1" />My Packs</TabsTrigger>
            <TabsTrigger value="brand" data-testid="ms-tab-brand"><UserCircle className="h-4 w-4 mr-1" />Personal Brand</TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="mt-4 space-y-5">
            <MediaPackGenerator onGenerated={(p) => setLatestPack(p)} onUsage={setUsage} />
            {latestPack && <PackCard pack={latestPack} usage={usage} onDeleted={() => setLatestPack(null)} />}
          </TabsContent>

          <TabsContent value="success" className="mt-4 space-y-5">
            <SuccessVisualGenerator />
          </TabsContent>

          <TabsContent value="packs" className="mt-4 space-y-4">
            {loadingPacks ? (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#B89B5E]" /></div>
            ) : packs.length === 0 ? (
              <Card className="border-dashed border-[#E8E6E1]"><CardContent className="py-16 text-center text-muted-foreground">
                No media packs yet. Head to <span className="font-medium text-[#1A1A1A]">Create</span> to make your first one.
              </CardContent></Card>
            ) : (
              packs.map((p) => <PackCard key={p.id} pack={p} usage={usage} onDeleted={(id) => setPacks((prev) => prev.filter((x) => x.id !== id))} />)
            )}
          </TabsContent>

          <TabsContent value="brand" className="mt-4">
            <PersonalBrandArchitect />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
